/**
 * iurop-product.js — lógica de la PDP (Glucody).
 * - Galería: miniaturas + flechas cambian la imagen activa.
 * - Variantes: swatches → variante activa → precio, CTA, stock, imagen.
 * - "Crea tu pack": los slots 1-3 fijan la cantidad (y se rellenan).
 * - Cantidad: stepper sincronizado con el precio del CTA y el pack.
 * - Añadir: intercepta el form → /cart/add.js → dispara `cart:updated`
 *   (iurop-cart-drawer.js re-pinta y abre el sidecart). Sin JS el form
 *   hace POST normal a /cart/add.
 * - Fecha de entrega: día de salida (hoy si el almacén trabaja; si no, el
 *   siguiente laborable) + min/max días naturales publicados por mercado.
 */
(function () {
  'use strict';

  function money(cents) {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  function initPdp(root) {
    if (!root || root.dataset.pdpInit) return;
    root.dataset.pdpInit = 'true';

    var variantsEl = root.querySelector('[data-pdp-variants]');
    var variants = [];
    try {
      variants = JSON.parse(variantsEl ? variantsEl.textContent : '[]');
    } catch (e) {}

    var images = root.querySelectorAll('[data-pdp-image]');
    var thumbs = root.querySelectorAll('[data-pdp-thumb]');
    var qtyInput = root.querySelector('[data-pdp-qty]');
    var ctaPrice = root.querySelector('[data-pdp-cta-price]');
    var priceEl = root.querySelector('[data-pdp-price]');
    var compareEl = root.querySelector('[data-pdp-compare]');
    var idInput = root.querySelector('[data-pdp-variant-id]');
    var addBtn = root.querySelector('[data-pdp-add]');
    var form = root.querySelector('[data-pdp-form]');
    var requiresVariant = root.getAttribute('data-pdp-require-variant') === 'true';
    var variantSelected = !requiresVariant;
    // Plantilla original del CTA (label · <span precio>) para restaurarla al
    // volver de una variante agotada: antes textContent='Agotado' destruía el
    // <span> del precio y el botón se quedaba "Agotado" y sin precio para siempre.
    var ctaTemplate = addBtn ? addBtn.innerHTML : '';
    var stickyBtnInit = root.querySelector('[data-pdp-sticky-add]');
    var stickyTemplate = stickyBtnInit ? stickyBtnInit.innerHTML : '';
    var currentImage = 0;
    var current = variants.length
      ? variants.find(function (v) { return String(v.id) === String(idInput && idInput.value); }) || variants[0]
      : null;

    // --- Fecha de entrega según la política publicada -----------------------
    // El plazo se cuenta desde la salida del almacén: 24–48 h en ES/PT/AD y
    // 2–5 días en DE/FR/IT. La política no publica hora de corte.
    var deliveryEl = root.querySelector('[data-pdp-delivery]');
    if (deliveryEl) {
      var minDays = parseInt(root.getAttribute('data-delivery-min'), 10) || 1;
      var maxDays = parseInt(root.getAttribute('data-delivery-max'), 10) || 2;
      var useHolidays = root.getAttribute('data-delivery-holidays') !== 'false';
      // Días cerrados. Se normalizan a AAAA-MM-DD porque el cliente escribe
      // "2026-8-5" con toda naturalidad y una comparación de cadenas lo
      // ignoraría EN SILENCIO (el almacén abriría un día que debía estar cerrado).
      var closed = (root.getAttribute('data-delivery-closed') || '')
        .split(/[\s,;]+/).filter(Boolean).map(function (v) {
          var m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (!m) return v;
          return m[1] + '-' + (m[2].length < 2 ? '0' : '') + m[2] + '-' + (m[3].length < 2 ? '0' : '') + m[3];
        });

      // Hora de Madrid sin librerías. Devuelve una fecha "local" que REPRESENTA
      // el reloj de Madrid; solo se usa para contar días y comparar con el
      // corte, nunca para convertir husos.
      function nowInSpain() {
        try {
          var parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit',
            day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
          }).formatToParts(new Date());
          var g = {};
          parts.forEach(function (p) { g[p.type] = p.value; });
          var h = parseInt(g.hour, 10);
          if (h === 24) h = 0; // algunos motores devuelven 24 a medianoche
          return new Date(+g.year, +g.month - 1, +g.day, h, +g.minute, 0, 0);
        } catch (e) {
          return new Date(); // navegador sin soporte de zonas: hora local
        }
      }

      function pad(n) { return (n < 10 ? '0' : '') + n; }
      function isoDate(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }

      // Festivos nacionales de España: 9 fijos + Viernes Santo, que se mueve
      // cada año y se calcula con el algoritmo gregoriano anónimo (así la lista
      // no caduca y nadie tiene que actualizarla en enero).
      var FIJOS = ['01-01', '01-06', '05-01', '08-15', '10-12', '11-01', '12-06', '12-08', '12-25'];
      function goodFriday(year) {
        var a = year % 19, b = Math.floor(year / 100), c = year % 100;
        var d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
        var g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
        var i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
        var m = Math.floor((a + 11 * h + 22 * l) / 451);
        var mo = Math.floor((h + l - 7 * m + 114) / 31);
        var da = ((h + l - 7 * m + 114) % 31) + 1;
        var easter = new Date(year, mo - 1, da);
        easter.setDate(easter.getDate() - 2);
        return easter;
      }
      function isHoliday(d) {
        if (closed.indexOf(isoDate(d)) !== -1) return true; // cierres del cliente
        if (!useHolidays) return false;
        if (FIJOS.indexOf(pad(d.getMonth() + 1) + '-' + pad(d.getDate())) !== -1) return true;
        var gf = goodFriday(d.getFullYear());
        return gf.getMonth() === d.getMonth() && gf.getDate() === d.getDate();
      }

      function isBiz(d) {
        if (d.getDay() === 0 || d.getDay() === 6) return false; // fines de semana
        return !isHoliday(d);
      }
      function nextBiz(d) {
        var x = new Date(d);
        // Tope de seguridad: si alguien pega meses enteros en "Días cerrados",
        // sin él este bucle no termina y cuelga la ficha.
        for (var guard = 0; guard < 400; guard++) {
          x.setDate(x.getDate() + 1);
          if (isBiz(x)) return x;
        }
        return x;
      }
      function addCalendarDays(from, n) {
        var d = new Date(from);
        d.setDate(d.getDate() + n);
        return d;
      }
      // Los plazos del perfil de envios estan en DIAS HABILES ("Entrega de 2 a
      // 7 dias habiles", "5 a 10 dias habiles", y un 24/48h de mensajeria que
      // tampoco reparte en domingo). Sumarlos como naturales hacia caer la
      // promesa en fin de semana el 29% de los dias del ano: un viernes con
      // plazo 1-2 anunciaba "sabado - domingo".
      function addBizDays(from, n) {
        var d = new Date(from);
        for (var i = 0; i < n; i++) d = nextBiz(d);
        return d;
      }

      var now = nowInSpain();
      var dispatch = new Date(now);
      if (!isBiz(dispatch)) dispatch = nextBiz(dispatch);

      // Dias HABILES desde la expedicion (ver addBizDays): es lo que dice el
      // perfil de envios y evita prometer entregas en sabado o domingo.
      var d1 = addBizDays(dispatch, minDays);
      var d2 = addBizDays(dispatch, maxDays);
      if (d2 < d1) { var swap = d1; d1 = d2; d2 = swap; } // por si alguien invierte los extras

      var dateLoc = document.documentElement.lang || 'es-ES';
      function fmtDay(d, withMonth) {
        if (withMonth) return d.toLocaleDateString(dateLoc, { day: 'numeric', month: 'long' });
        return dayOnly(d);
      }
      // El día SUELTO tal y como lo escribe ese idioma dentro de una fecha
      // completa, arrastrando el signo que lo acompaña. En alemán el día lleva
      // punto ordinal ("20. August") y con `{day:'numeric'}` a secas salía
      // «20 - 21. August», con el primero cojo.
      function dayOnly(d) {
        try {
          var parts = new Intl.DateTimeFormat(dateLoc, { day: 'numeric', month: 'long' }).formatToParts(d);
          for (var i = 0; i < parts.length; i++) {
            if (parts[i].type !== 'day') continue;
            var out = parts[i].value;
            var next = parts[i + 1];
            if (next && next.type === 'literal' && /^[.º]/.test(next.value)) out += next.value.charAt(0);
            return out;
          }
        } catch (e) {}
        return String(d.getDate());
      }
      // ¿Este idioma pone el MES antes que el día? En inglés sí ("August 20"),
      // y al imprimir siempre día-suelto + día-con-mes el mes acababa EN MEDIO
      // del rango: «20 - August 21». Se le pregunta al propio Intl en vez de
      // mantener una lista de idiomas a mano.
      var monthFirst = false;
      try {
        var order = new Intl.DateTimeFormat(dateLoc, { day: 'numeric', month: 'long' }).formatToParts(d1);
        for (var oi = 0; oi < order.length; oi++) {
          if (order[oi].type === 'month') { monthFirst = true; break; }
          if (order[oi].type === 'day') break;
        }
      } catch (e) {}

      // Abanico de DOS días unidos con guion: «20 - 21 de agosto» / «August 20 - 21».
      if (isoDate(d1) === isoDate(d2)) {
        deliveryEl.textContent = fmtDay(d1, true); // solo si el cliente pone min == max
      } else if (d1.getMonth() !== d2.getMonth()) {
        deliveryEl.textContent = fmtDay(d1, true) + ' - ' + fmtDay(d2, true);
      } else {
        deliveryEl.textContent = monthFirst
          ? fmtDay(d1, true) + ' - ' + fmtDay(d2, false)
          : fmtDay(d1, false) + ' - ' + fmtDay(d2, true);
      }
    }

    // --- Galería ---
    var thumbsWrap = root.querySelector('[data-pdp-thumbs]');
    function showImage(index) {
      if (!images.length) return;
      // QA 15-jul: SIN bucle — se clava en los extremos; la flecha izquierda
      // se oculta en la 1ª imagen y la derecha en la última.
      currentImage = Math.max(0, Math.min(index, images.length - 1));
      images.forEach(function (img, i) { img.classList.toggle('is-active', i === currentImage); });
      // la miniatura activa se marca por su índice REAL (data-pdp-thumb),
      // no por posición en la lista, y se CENTRA en su carril si asoma fuera
      thumbs.forEach(function (t) {
        var idx = parseInt(t.getAttribute('data-pdp-thumb'), 10);
        var active = idx === currentImage;
        t.classList.toggle('is-active', active);
        if (active && thumbsWrap) {
          var tr = t.getBoundingClientRect();
          var wr = thumbsWrap.getBoundingClientRect();
          if (tr.left < wr.left || tr.right > wr.right) {
            thumbsWrap.scrollTo({ left: t.offsetLeft - (thumbsWrap.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
          }
        }
      });
      if (prev) prev.hidden = currentImage === 0;
      if (next) next.hidden = currentImage === images.length - 1;
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showImage(parseInt(t.getAttribute('data-pdp-thumb'), 10)); });
    });
    var prev = root.querySelector('[data-pdp-prev]');
    var next = root.querySelector('[data-pdp-next]');
    if (prev) prev.addEventListener('click', function () { showImage(currentImage - 1); });
    if (next) next.addEventListener('click', function () { showImage(currentImage + 1); });
    if (images.length) showImage(currentImage);

    // QA: swipe táctil en la galería (móvil) — deslizar cambia de imagen.
    var stage = root.querySelector('.glucody-pdp__stage');
    if (stage && images.length > 1) {
      var swipeX = null, swipeY = null;
      stage.addEventListener('touchstart', function (e) {
        swipeX = e.changedTouches[0].clientX;
        swipeY = e.changedTouches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (swipeX === null) return;
        var dx = e.changedTouches[0].clientX - swipeX;
        var dy = e.changedTouches[0].clientY - swipeY;
        // umbral 40px y más horizontal que vertical → no choca con el tap→lightbox
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          showImage(currentImage + (dx < 0 ? 1 : -1));
        }
        swipeX = swipeY = null;
      }, { passive: true });
    }

    // QA: clic en el rating del título → scroll suave a las reseñas.
    var ratingEls = root.querySelectorAll('.glucody-pdp__rating');
    ratingEls.forEach(function (el) {
      el.addEventListener('click', function () {
        var reviews = document.querySelector('.glucody-testimonials');
        if (reviews) reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // --- Lightbox de vídeo UGC ("Lo que dicen") ---
    // Cada tarjeta con vídeo lleva un <template> con el reproductor ya montado
    // (video de Shopify o iframe YouTube/Vimeo). Al hacer clic se clona al
    // lightbox; al cerrar se vacía el stage, lo que detiene la reproducción.
    var ugcLightbox = root.querySelector('[data-ugc-lightbox]');
    if (ugcLightbox) {
      var ugcStage = ugcLightbox.querySelector('[data-ugc-stage]');
      var ugcClose = function () {
        if (ugcStage) ugcStage.innerHTML = '';
        ugcLightbox.hidden = true;
      };
      root.querySelectorAll('[data-ugc-open]').forEach(function (card) {
        card.addEventListener('click', function () {
          var tpl = card.querySelector('[data-ugc-video]');
          if (!tpl || !ugcStage) return;
          ugcStage.innerHTML = '';
          ugcStage.appendChild(tpl.content.cloneNode(true));
          ugcLightbox.hidden = false;
        });
      });
      ugcLightbox.addEventListener('click', function (e) {
        if (e.target === ugcLightbox || e.target.closest('[data-ugc-close]')) ugcClose();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !ugcLightbox.hidden) ugcClose();
      });
    }

    // "También podrías necesitar" → recomendaciones related (mín. 6). Se re-pide
    // la sección vía el endpoint de recomendaciones y se sustituyen SOLO los
    // upsells (mismo diseño, en la columna de info). quick-add sigue delegado.
    var upsellsBox = root.querySelector('[data-pdp-upsells]');
    var recoSectionId = root.getAttribute('data-section-id');
    var recoProductId = root.getAttribute('data-product-id');
    var recoBase = window.routes && window.routes.product_recommendations_url;
    if (upsellsBox && recoSectionId && recoProductId && recoBase) {
      var upsellUrl = recoBase + '?section_id=' + encodeURIComponent(recoSectionId) +
        '&product_id=' + encodeURIComponent(recoProductId) + '&intent=related&limit=6';
      fetch(upsellUrl, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-pdp-upsells]');
          if (fresh && fresh.querySelector('.glucody-pdp__upsell')) {
            upsellsBox.innerHTML = fresh.innerHTML;
          }
        })
        .catch(function () {});
    }

    // --- Cantidad + precios ---
    function getQty() {
      var q = parseInt(qtyInput && qtyInput.value, 10);
      return isNaN(q) || q < 1 ? 1 : q;
    }

    var stickyQty = root.querySelector('[data-pdp-sticky-qty]');
    var stickyPrice = root.querySelector('[data-pdp-sticky-price]');

    function syncPrices() {
      if (!current) return;
      var qty = getQty();
      if (priceEl) priceEl.textContent = money(current.price);
      if (compareEl) {
        compareEl.textContent = current.compare_at_price > current.price ? money(current.compare_at_price) : '';
        compareEl.hidden = !(current.compare_at_price > current.price);
      }
      var total = current.price * qty + packTotal();
      if (ctaPrice) ctaPrice.textContent = money(total);
      if (stickyPrice) stickyPrice.textContent = money(total);
      if (stickyQty) stickyQty.textContent = qty;
    }

    function setQty(q) {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, q);
      syncPack();
      syncPrices();
    }

    var down = root.querySelector('[data-pdp-qty-down]');
    var up = root.querySelector('[data-pdp-qty-up]');
    if (down) down.addEventListener('click', function () { setQty(getQty() - 1); });
    if (up) up.addEventListener('click', function () { setQty(getQty() + 1); });
    if (qtyInput) qtyInput.addEventListener('change', function () { setQty(getQty()); });

    // --- Crea tu pack (elegir productos para los slots 2 y 3) ---
    var packWrap = root.querySelector('[data-pdp-pack]');
    var picker = root.querySelector('[data-pdp-pack-picker]');
    var activeSlot = null; // slot que se está rellenando

    function syncPack() {} // compat: el pack ya no depende de la cantidad

    function openPicker(slot) {
      if (!picker) return;
      activeSlot = slot;
      picker.hidden = false;
    }
    function closePicker() {
      if (picker) picker.hidden = true;
      activeSlot = null;
    }

    if (packWrap) {
      packWrap.addEventListener('click', function (e) {
        // abrir picker desde un slot vacío (no fijo)
        var slot = e.target.closest('[data-pdp-slot]:not([data-pdp-slot-fixed])');
        var remove = e.target.closest('[data-pdp-slot-remove]');
        var addOpt = e.target.closest('[data-pdp-pack-add]');
        var closeBtn = e.target.closest('[data-pdp-pack-picker-close]');

        if (closeBtn) { closePicker(); return; }

        if (remove) {
          var s = remove.closest('[data-pdp-slot]');
          s.classList.remove('is-filled');
          s.removeAttribute('data-variant-id');
          var img = s.querySelector('[data-pdp-slot-img]');
          var plus = s.querySelector('[data-pdp-slot-plus]');
          var name = s.querySelector('[data-pdp-slot-name]');
          if (img) { img.hidden = true; img.removeAttribute('src'); }
          if (plus) plus.hidden = false;
          if (name) name.textContent = s.getAttribute('data-pdp-slot') + (window.GLUCODY_I18N && window.GLUCODY_I18N.slot || 'º Producto');
          s.removeAttribute('data-price-cents');
          remove.hidden = true;
          syncPrices();
          return;
        }

        if (addOpt && activeSlot) {
          activeSlot.setAttribute('data-variant-id', addOpt.getAttribute('data-variant-id'));
          activeSlot.setAttribute('data-price-cents', addOpt.getAttribute('data-price-cents') || '0');
          activeSlot.classList.add('is-filled');
          var img2 = activeSlot.querySelector('[data-pdp-slot-img]');
          var plus2 = activeSlot.querySelector('[data-pdp-slot-plus]');
          var name2 = activeSlot.querySelector('[data-pdp-slot-name]');
          var rem2 = activeSlot.querySelector('[data-pdp-slot-remove]');
          if (img2) { img2.src = addOpt.getAttribute('data-img'); img2.hidden = false; }
          if (plus2) plus2.hidden = true;
          if (name2) name2.textContent = addOpt.getAttribute('data-name');
          if (rem2) rem2.hidden = false;
          closePicker();
          syncPrices();
          return;
        }

        if (slot && !addOpt) { openPicker(slot); }
      });
    }

    function packItems() {
      // variantes elegidas en los slots 2 y 3
      var items = [];
      root.querySelectorAll('[data-pdp-slot][data-variant-id]:not([data-pdp-slot-fixed])').forEach(function (s) {
        var vid = s.getAttribute('data-variant-id');
        if (vid) items.push({ id: Number(vid), quantity: 1 });
      });
      return items;
    }

    function packTotal() {
      // suma en céntimos de los productos elegidos en los slots (no fijos)
      var total = 0;
      root.querySelectorAll('[data-pdp-slot][data-price-cents]:not([data-pdp-slot-fixed])').forEach(function (s) {
        total += parseInt(s.getAttribute('data-price-cents'), 10) || 0;
      });
      return total;
    }

    // --- Variantes ---
    function selectedOptions() {
      var opts = [];
      root.querySelectorAll('[data-pdp-option]').forEach(function (row, i) {
        var active = row.querySelector('.glucody-pdp__swatch.is-active');
        opts[i] = active ? active.getAttribute('data-pdp-value') : null;
      });
      return opts;
    }

    // Pone/quita el estado "Agotado" en un botón CTA sin perder el <span> del
    // precio: al agotar guarda el flag y escribe el texto localizado; al volver
    // a disponible restaura la plantilla original (label · precio).
    function setCtaState(btn, template, avail) {
      if (!btn) return;
      btn.disabled = !avail;
      btn.setAttribute('aria-disabled', avail ? 'false' : 'true');
      if (avail) {
        if (btn.dataset.soldout === '1') {
          btn.innerHTML = template;
          btn.dataset.soldout = '';
        }
      } else if (btn.dataset.soldout !== '1') {
        btn.dataset.soldout = '1';
        btn.textContent = btn.getAttribute('data-pdp-soldout-text') || 'Agotado';
      }
    }

    function applyVariant(v) {
      if (!v) return;
      current = v;
      if (idInput) idInput.value = v.id;
      var stickyAddBtn = root.querySelector('[data-pdp-sticky-add]');
      setCtaState(addBtn, ctaTemplate, v.available);
      setCtaState(stickyAddBtn, stickyTemplate, v.available);
      // el innerHTML pudo reconstruirse → recuperar refs de los spans de precio
      ctaPrice = root.querySelector('[data-pdp-cta-price]');
      stickyPrice = root.querySelector('[data-pdp-sticky-price]');
      if (v.media_index !== null && v.media_index !== undefined) showImage(v.media_index);
      syncPrices();
      var stock = root.querySelector('[data-pdp-stock]');
      if (stock) {
        var lowThresh = parseInt(stock.getAttribute('data-pdp-low-threshold'), 10) || 0;
        var isLow = v.available && v.inventory_management === 'shopify' &&
          v.inventory_quantity > 0 && v.inventory_quantity <= lowThresh;
        stock.classList.toggle('glucody-pdp__stock--out', !v.available);
        stock.classList.toggle('glucody-pdp__stock--low', isLow);
        var stockLabel = stock.querySelector('[data-pdp-stock-label]');
        var stockDelivery = stock.querySelector('[data-pdp-stock-delivery]');
        if (stockLabel) {
          if (!v.available) stockLabel.textContent = stock.getAttribute('data-pdp-soldout-text') || 'Agotado';
          else if (isLow) stockLabel.textContent = (stock.getAttribute('data-pdp-low-text') || '').replace('[count]', v.inventory_quantity);
          else stockLabel.textContent = stock.getAttribute('data-pdp-instock-text') || 'En stock';
        }
        if (stockDelivery) stockDelivery.hidden = !v.available;
      }
    }

    root.querySelectorAll('[data-pdp-option]').forEach(function (row) {
      row.addEventListener('click', function (e) {
        var swatch = e.target.closest('.glucody-pdp__swatch');
        if (!swatch) return;
        row.classList.remove('is-error');
        row.removeAttribute('aria-invalid');
        row.querySelectorAll('.glucody-pdp__swatch').forEach(function (s) {
          s.classList.toggle('is-active', s === swatch);
          s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
        });
        var label = row.querySelector('[data-pdp-option-value]');
        if (label) label.textContent = swatch.getAttribute('data-pdp-value');
        var opts = selectedOptions();
        variantSelected = !requiresVariant || opts.every(function (value) { return value !== null; });
        var match = variants.find(function (v) {
          return v.options.every(function (o, i) { return opts[i] === null || o === opts[i]; });
        });
        if (variantSelected) applyVariant(match);
      });
    });

    // --- Añadir al carrito (AJAX) ---
    function showVariantError() {
      var row = root.querySelector('[data-pdp-option]');
      if (!row) return;
      row.classList.add('is-error');
      row.setAttribute('aria-invalid', 'true');
      var label = row.querySelector('[data-pdp-option-value]');
      if (label) label.textContent = root.getAttribute('data-pdp-select-size-text') || 'Selecciona una talla';
      var firstSwatch = row.querySelector('.glucody-pdp__swatch');
      if (firstSwatch) firstSwatch.focus({ preventScroll: true });
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function addToCart() {
      if (!addBtn || addBtn.classList.contains('is-loading')) return;
      if (!variantSelected || !idInput || !idInput.value) {
        showVariantError();
        return;
      }
      if (addBtn.disabled) return;
      // Guardia del pack de muestras (iurop-cart-drawer.js): pack solo o nada.
      if (window.GlucodyCartGuard && !window.GlucodyCartGuard.check(idInput.value)) return;
      addBtn.classList.add('is-loading');
      var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      var items = [{ id: Number(idInput.value), quantity: getQty() }].concat(packItems());
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart/add failed');
          return res.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(function () {
          if (form) form.submit();
        })
        .then(function () {
          addBtn.classList.remove('is-loading');
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        addToCart();
      });
    }

    // --- Sticky add-to-cart ---
    var sticky = root.querySelector('[data-pdp-sticky]');
    if (sticky) {
      var stickyDown = sticky.querySelector('[data-pdp-sticky-down]');
      var stickyUp = sticky.querySelector('[data-pdp-sticky-up]');
      var stickyAdd = sticky.querySelector('[data-pdp-sticky-add]');
      if (stickyDown) stickyDown.addEventListener('click', function () { setQty(getQty() - 1); });
      if (stickyUp) stickyUp.addEventListener('click', function () { setQty(getQty() + 1); });
      if (stickyAdd) stickyAdd.addEventListener('click', addToCart);

      // La barra aparece solo cuando el visitante ya ha dejado atras el boton real
      // de compra [data-pdp-add] por arriba. Con el boton todavia por debajo del
      // pliegue (movil nada mas aterrizar, galeria delante) NO aparece.
      // Ganadora del test PDP - Sticky ATC (S1-01) en Intelligems, cerrado el
      // 15-sep-2026. Antes salia tambien con el boton por debajo.
      // HISTERESIS: aparece cuando el boton ha salido del todo por arriba y solo se
      // oculta cuando vuelve a estar comodamente dentro (banda de 48px), asi no
      // parpadea en el limite. No toca el DOM si el estado no cambia.
      // Scroll+rAF (IntersectionObserver no dispara con overflow:clip).
      var stickyTarget = addBtn || form;
      var ticking = false;
      var stuck = false;

      var stickyFooter = document.querySelector('.glucody-footer');
      function evalSticky() {
        ticking = false;
        if (!stickyTarget) return;
        var r = stickyTarget.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var offscreen = stuck
          ? r.bottom < 48
          : r.bottom < 0;
        // QA 15-jul: la barra se esconde cuando el footer entra en pantalla
        // (no se superpone ni lo pisa)
        if (stickyFooter && stickyFooter.getBoundingClientRect().top < vh - 24) {
          offscreen = false;
        }
        if (offscreen === stuck) return;
        stuck = offscreen;
        sticky.classList.toggle('is-visible', offscreen);
        document.body.classList.toggle('sticky-atc-visible', offscreen);
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(evalSticky);
        }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      evalSticky();
    }

    // --- Lightbox de galería (clic en la foto → galería a pantalla completa) ---
    var lightbox = root.querySelector('[data-pdp-lightbox]');
    if (lightbox && images.length) {
      var lbImg = lightbox.querySelector('[data-lightbox-img]');
      var lbCount = lightbox.querySelector('[data-lightbox-count]');
      var lbIndex = 0;

      function lbSources() {
        return Array.prototype.map.call(images, function (wrap) {
          var img = wrap.querySelector('img');
          return img ? (img.currentSrc || img.src) : '';
        });
      }

      function lbShow(i) {
        var srcs = lbSources();
        lbIndex = ((i % srcs.length) + srcs.length) % srcs.length;
        if (lbImg) lbImg.src = srcs[lbIndex];
        if (lbCount) lbCount.textContent = (lbIndex + 1) + ' / ' + srcs.length;
      }

      function lbOpen(i) {
        lbShow(i);
        lightbox.hidden = false;
        document.documentElement.setAttribute('data-lightbox-open', 'true');
      }

      function lbClose() {
        lightbox.hidden = true;
        document.documentElement.removeAttribute('data-lightbox-open');
      }

      root.querySelectorAll('[data-pdp-image]').forEach(function (wrap, i) {
        wrap.addEventListener('click', function () { lbOpen(currentImage || i); });
      });

      lightbox.addEventListener('click', function (e) {
        if (e.target.closest('[data-lightbox-close]')) { lbClose(); return; }
        if (e.target.closest('[data-lightbox-prev]')) { lbShow(lbIndex - 1); return; }
        if (e.target.closest('[data-lightbox-next]')) { lbShow(lbIndex + 1); return; }
        if (!e.target.closest('.glucody-lightbox__stage')) lbClose();
      });

      document.addEventListener('keydown', function (e) {
        if (lightbox.hidden) return;
        if (e.key === 'Escape') lbClose();
        if (e.key === 'ArrowLeft') lbShow(lbIndex - 1);
        if (e.key === 'ArrowRight') lbShow(lbIndex + 1);
      });
    }

    // --- Descripción "Leer más" (solo si desborda 2 líneas) ---
    var descWrap = root.querySelector('[data-pdp-desc]');
    if (descWrap) {
      var descBody = descWrap.querySelector('[data-pdp-desc-body]');
      var descToggle = descWrap.querySelector('[data-pdp-desc-toggle]');
      if (descBody && descToggle) {
        // desborda si el alto real (sin clamp) supera al clamp de 2 líneas
        if (descBody.scrollHeight - descBody.clientHeight > 2) {
          descToggle.hidden = false;
        }
        descToggle.addEventListener('click', function () {
          var open = descWrap.classList.toggle('is-open');
          descToggle.textContent = open ? descToggle.getAttribute('data-less') : descToggle.getAttribute('data-more');
        });
      }
    }

    syncPrices();
    syncPack();
  }

  function initAll() {
    document.querySelectorAll('[data-pdp]').forEach(initPdp);
  }

  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', initAll);
  }

  // --- Selector de familia (sabores/colores = productos separados): swap
  // AJAX para que cambiar de sabor se sienta como cambiar de variante.
  // Trae la página del hermano (prefetch al pasar el ratón), reemplaza SOLO
  // las secciones de <main> cuyo HTML cambia (la PDP, pestañas, FAQs…; las
  // estáticas ni se tocan, sin parpadeo) y actualiza la URL con pushState.
  // Los listeners van delegados en document, así sobreviven al swap; los JS
  // por sección se re-enganchan con el evento shopify:section:load.
  var famCache = {};

  function famFetch(url) {
    if (!famCache[url]) {
      famCache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      }).catch(function (err) {
        delete famCache[url];
        throw err;
      });
    }
    return famCache[url];
  }

  function famSwap(url, push) {
    return famFetch(url).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var changed = false;
      document.querySelectorAll('main .shopify-section').forEach(function (sec) {
        var fresh = doc.getElementById(sec.id);
        if (fresh && fresh.innerHTML !== sec.innerHTML) {
          sec.replaceWith(fresh);
          changed = true;
        }
      });
      // pase lo que pase, ningún selector se queda atenuado (is-loading)
      document.querySelectorAll('[data-pdp-family].is-loading').forEach(function (el) {
        el.classList.remove('is-loading');
      });
      if (!changed) return;
      document.title = doc.title;
      if (push) {
        if (!history.state || !history.state.pdpFam) {
          history.replaceState({ pdpFam: true }, '', window.location.href);
        }
        history.pushState({ pdpFam: true }, '', url);
      }
      document.dispatchEvent(new Event('shopify:section:load'));
    });
  }

  function famLink(target) {
    return target.closest ? target.closest('[data-pdp-family] a[href]') : null;
  }

  document.addEventListener('pointerover', function (e) {
    var a = famLink(e.target);
    if (a && !a.classList.contains('is-active')) famFetch(a.href);
  });
  document.addEventListener('touchstart', function (e) {
    var a = famLink(e.target);
    if (a && !a.classList.contains('is-active')) famFetch(a.href);
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var a = famLink(e.target);
    if (!a) return;
    // respetar abrir-en-pestaña-nueva (Cmd/Ctrl/Shift/clic central)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    if (a.classList.contains('is-active')) return;
    var wrap = a.closest('[data-pdp-family]');
    if (wrap) wrap.classList.add('is-loading');
    famSwap(a.href, true).catch(function () {
      window.location.href = a.href; // fallback: navegación normal
    });
  });

  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.pdpFam) {
      famSwap(window.location.href, false).catch(function () {
        window.location.reload();
      });
    }
  });
})();
